import { useMemo } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { MultiSelect, type MultiSelectOption } from './MultiSelect'
import type { AnalyticsFilters } from '../../hooks/useAnalytics'

/**
 * The single filter contract, shared by every view.
 *
 * State lives in the URL so any view is deep-linkable — a share page can link
 * straight to `/analytics?shareId=49`, and an operator can paste a filtered
 * view to someone else.
 */

export interface FilterOption {
  value: string
  label: string
}

export interface FilterBarProps {
  filters: AnalyticsFilters
  onChange: (next: Partial<AnalyticsFilters>) => void
  seeds: FilterOption[]
  shares: FilterOption[]
  workspaces: FilterOption[]
  apps: MultiSelectOption[]
  versions: MultiSelectOption[]
  users: MultiSelectOption[]
  devices: MultiSelectOption[]
  platforms: MultiSelectOption[]
  countries: MultiSelectOption[]
}

/** Relative windows, resolved at render so "last 7 days" means now. */
const RANGES = [
  { key: '24h', label: '24h', days: 1 },
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '1y', label: '1 year', days: 365 },
  { key: 'all', label: 'All time', days: 0 },
] as const

export function rangeToFilters(days: number): { from: string; to: string } {
  const to = new Date()
  // "All time" still sends explicit bounds: every endpoint requires them, so an
  // unbounded dashboard query can never become a full scan by accident.
  const from = days > 0
    ? new Date(to.getTime() - days * 86_400_000)
    : new Date('2026-01-01T00:00:00.000Z')
  return { from: from.toISOString(), to: to.toISOString() }
}

function Select({ label, value, options, onChange, placeholder }: {
  label: string
  value: string | undefined
  options: FilterOption[]
  onChange: (value: string | undefined) => void
  placeholder: string
}) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <select
        className="h-9 rounded-md border border-input bg-background px-2 text-sm min-w-[9rem] max-w-[14rem]"
        value={value ?? ''}
        onChange={e => onChange(e.target.value || undefined)}
      >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}

export function FilterBar({
  filters, onChange, seeds, shares, workspaces, apps, versions, users, devices, platforms, countries,
}: FilterBarProps) {
  const activeRange = useMemo(() => {
    if (!filters.from || !filters.to) return '30d'
    const span = Date.parse(filters.to) - Date.parse(filters.from)
    const days = Math.round(span / 86_400_000)
    return RANGES.find(r => r.days === days)?.key ?? 'custom'
  }, [filters.from, filters.to])

  const activeCount =
    [filters.seed, filters.shareId, filters.workspaceId].filter(Boolean).length +
    [filters.app, filters.version, filters.email, filters.device, filters.country]
      .reduce((n, list) => n + (list?.length ?? 0), 0)

  return (
    <Card className="mb-6">
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map(r => (
            <Button
              key={r.key}
              size="sm"
              variant={activeRange === r.key ? 'default' : 'outline'}
              onClick={() => onChange(rangeToFilters(r.days))}
            >
              {r.label}
            </Button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="size-4 accent-current"
                checked={!!filters.excludeInternal}
                onChange={e => onChange({ excludeInternal: e.target.checked })}
              />
              Exclude internal
            </label>
            {activeCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onChange({
                  seed: undefined, shareId: undefined, workspaceId: undefined,
                  app: undefined, version: undefined, email: undefined,
                  device: undefined, country: undefined,
                })}
              >
                <X className="size-3.5 mr-1" />
                Clear {activeCount}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Seed, share and workspace stay single-select: they are a
              hierarchy, and picking two shares at once has no useful meaning
              when the one below it narrows by the one above. */}
          <Select
            label="Seed" placeholder="All seeds" value={filters.seed} options={seeds}
            // Narrowing the seed invalidates any share/workspace below it.
            onChange={v => onChange({ seed: v, shareId: undefined, workspaceId: undefined })}
          />
          <Select
            label="Share" placeholder="All shares"
            value={filters.shareId ? String(filters.shareId) : undefined} options={shares}
            onChange={v => onChange({ shareId: v ? Number(v) : undefined, workspaceId: undefined })}
          />
          <Select
            label="Workspace" placeholder="All workspaces"
            value={filters.workspaceId ? String(filters.workspaceId) : undefined} options={workspaces}
            onChange={v => onChange({ workspaceId: v ? Number(v) : undefined })}
          />

          <MultiSelect
            label="App" placeholder="All apps" className="w-[11rem]"
            options={apps} selected={filters.app ?? []}
            onChange={v => onChange({ app: v.length ? v : undefined })}
          />
          <MultiSelect
            label="Version" placeholder="All versions" className="w-[10rem]"
            options={versions} selected={filters.version ?? []}
            onChange={v => onChange({ version: v.length ? v : undefined })}
          />
          <MultiSelect
            label="User" placeholder="All users" className="w-[16rem]"
            options={users} selected={filters.email ?? []}
            onChange={v => onChange({ email: v.length ? v : undefined })}
          />
          <MultiSelect
            label="Device" placeholder="All devices" className="w-[10rem]"
            options={devices} selected={filters.device ?? []}
            onChange={v => onChange({ device: v.length ? v : undefined })}
          />
          <MultiSelect
            label="Platform" placeholder="All platforms" className="w-[12rem]"
            options={platforms} selected={filters.platform ?? []}
            onChange={v => onChange({ platform: v.length ? v : undefined })}
          />
          <MultiSelect
            label="Country" placeholder="All countries" className="w-[10rem]"
            options={countries} selected={filters.country ?? []}
            onChange={v => onChange({ country: v.length ? v : undefined })}
          />
        </div>
      </CardContent>
    </Card>
  )
}
